import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import FranceMap from '../components/FranceMap';
import { getAirQuality } from '../Services/epidemicService';
import {
  Wind,
  MapPin,
  RefreshCw,
  Loader2,
  Info,
  Clock,
  ExternalLink,
  AlertCircle,
  BookOpen,
  Leaf
} from 'lucide-react';

// ── Configuration ──────────────────────────────────────────────────────────

const AQI_META = [
  { aqi: 1, label: 'Bon',         color: '#10B981' },
  { aqi: 2, label: 'Moyen',       color: '#84CC16' },
  { aqi: 3, label: 'Dégradé',     color: '#F59E0B' },
  { aqi: 4, label: 'Mauvais',      color: '#EF4444' },
  { aqi: 5, label: 'Très mauvais', color: '#7C3AED' },
];

const POLLUTANTS = [
  { key: 'pm25', label: 'PM 2.5',  desc: 'Poussières ultra-fines',  unit: 'μg/m³', detail: 'Taille : 1/30ème d’un cheveu. Pénètrent dans le sang.' },
  { key: 'pm10', label: 'PM 10',   desc: 'Poussières fines',       unit: 'μg/m³', detail: 'Proviennent du chauffage, des chantiers et du freinage.' },
  { key: 'no2',  label: 'NO₂',    desc: 'Gaz d’échappement',       unit: 'μg/m³', detail: 'Indique la proximité d’un fort trafic routier.' },
  { key: 'o3',   label: 'O₃',     desc: 'Ozone de chaleur',        unit: 'μg/m³', detail: 'Se forme en été sous l’effet du soleil intense.' },
];

const POLLEN_TYPES = [
  { key: 'alder',   name: 'Aulne',      emoji: '🌳' },
  { key: 'birch',   name: 'Bouleau',    emoji: '🌲' },
  { key: 'grass',   name: 'Graminées',  emoji: '🌾' },
  { key: 'mugwort', name: 'Armoise',    emoji: '🌿' },
  { key: 'olive',   name: 'Olivier',    emoji: '🫒' },
  { key: 'ragweed', name: 'Ambroisie',  emoji: '🌻' },
];

const REGION_NAMES = {
  'ile-de-france': 'Île-de-France',
  'auvergne-rhone-alpes': 'Auvergne-Rhône-Alpes',
  'bourgogne-franche-comte': 'Bourgogne-Franche-Comté',
  'bretagne': 'Bretagne',
  'centre-val-de-loire': 'Centre-Val de Loire',
  'grand-est': 'Grand Est',
  'hauts-de-france': 'Hauts-de-France',
  'normandie': 'Normandie',
  'nouvelle-aquitaine': 'Nouvelle-Aquitaine',
  'occitanie': 'Occitanie',
  'pays-de-la-loire': 'Pays de la Loire',
  'provence-alpes-cote-d-azur': "Provence-Alpes-Côte d'Azur",
  'corse': 'Corse',
};

// ── Composant ────────────────────────────────────────────────────────────────

const HealthAlertsPage = () => {
  const [airData, setAirData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState('ile-de-france');
  const [lastUpdate, setLastUpdate] = useState(null);

  

  useEffect(() => {
    fetchAirQuality();
  }, []);

  const fetchAirQuality = async () => {
    setLoading(true);
    try {
      const data = await getAirQuality();
      setAirData(data.regions);
      setLastUpdate(new Date(data.lastUpdate));
    } catch (error) {
      console.error('Erreur données:', error);
    }
    setLoading(false);
  };

  const currentData = airData?.[selectedRegion];

  const renderTooltipContent = (regionId) => {
    const d = airData?.[regionId];
    if (!d) return null;
    return (
      <div className="p-1">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
          <span className="text-xs font-bold" style={{ color: d.color }}>{d.label}</span>
        </div>
        {d.pollen && (
          <p className="text-[10px] text-gray-500 dark:text-gray-400">
            🌿 {d.pollen.dominantLabel}
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="animate-spin text-blue-500 w-10 h-10" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">

        {/* --- HEADER --- */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 dark:border-gray-800 pb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Wind className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Santé & Environnement</h1>
              <p className="text-gray-500 flex items-center gap-2 italic">
                <Clock className="w-4 h-4" /> Mis à jour : {lastUpdate?.toLocaleTimeString('fr-FR')}
              </p>
            </div>
          </div>
          <Button onClick={fetchAirQuality} className="bg-blue-600 hover:bg-blue-700 text-white transition-all active:scale-95 shadow-md">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        </div>

        {/* --- MÉMO PÉDAGOGIQUE --- */}
        <Card className="p-4 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-950/20 dark:to-blue-950/20 border-none shadow-sm">
          <div className="flex gap-4 items-center text-sm">
            <BookOpen className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <p className="text-emerald-900 dark:text-emerald-300">
              <b>Mémo :</b> La pollution chimique (gaz) et biologique (pollens) sont deux mesures différentes. 
              Un ciel bleu peut cacher des pollens, et un air pollué peut être pauvre en allergènes. Vérifiez les deux indices !
            </p>
          </div>
        </Card>

        {/* --- SECTION 1 : CARTE ET POLLUTION --- */}
        <div className="grid lg:grid-cols-2 gap-8">
          <Card className="p-6 overflow-hidden">
            <div className="flex items-center gap-2 mb-6 text-blue-600 font-bold uppercase tracking-wider text-xs">
               <MapPin className="w-4 h-4" /> Qualité de l'Air (AQI)
            </div>
            <FranceMap
              regionData={airData}
              onRegionClick={(id) => setSelectedRegion(id)}
              selectedRegion={selectedRegion}
              colorMode="direct"
              renderTooltipContent={renderTooltipContent}
            />
            <div className="mt-8 grid grid-cols-5 gap-1">
              {AQI_META.map(m => (
                <div key={m.aqi} className="flex flex-col items-center gap-1">
                  <div className="h-1.5 w-full rounded-full" style={{ backgroundColor: m.color }} />
                  <span className="text-[10px] text-gray-400 font-medium">{m.label}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 border-l-4" style={{ borderLeftColor: currentData?.color || '#eee' }}>
            <h2 className="text-xl font-bold mb-6 text-gray-900 dark:text-gray-100">
              {REGION_NAMES[selectedRegion]}
            </h2>
            
            {currentData ? (
              <div className="space-y-6">
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <p className="text-sm font-bold uppercase mb-1" style={{ color: currentData.color }}>Indice {currentData.label}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Condition actuelle : {currentData.aqi}/5. Limitez l'exposition prolongée en zone urbaine si l'indice dépasse 3.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {POLLUTANTS.map(p => (
                    <div key={p.key} className="p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                      <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">{p.label}</p>
                      <p className="text-lg font-bold text-blue-600">{currentData[p.key]} <span className="text-xs font-normal opacity-50">{p.unit}</span></p>
                      <p className="text-[10px] text-gray-400 mt-1 italic">{p.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-gray-400 italic">Aucune donnée pour cette zone.</p>}
          </Card>
        </div>

        {/* --- SECTION 2 : LES POLLENS --- */}
        <Card className="p-8 border-2 border-emerald-100 dark:border-emerald-900/30">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                <Leaf className="w-6 h-6" /> Alertes Pollens
              </h2>
              <p className="text-sm text-gray-500">Risque allergique actuel pour : <b>{REGION_NAMES[selectedRegion]}</b></p>
            </div>
            
            {currentData?.pollen && (
              <div className="px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 text-sm font-bold border border-emerald-200">
                Dominant : {currentData.pollen.dominantLabel}
              </div>
            )}
          </div>

          {currentData?.pollen ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {POLLEN_TYPES.map(type => {
                const p = currentData.pollen[type.key];
                if (!p) return null;
                return (
                  <div key={type.key} className="space-y-2 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-bold text-gray-800 dark:text-gray-100">{type.emoji} {type.name}</span>
                      <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: p.color }}>
                        {p.label}
                      </span>
                    </div>
                    <div className="relative h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="absolute top-0 left-0 h-full transition-all duration-700"
                        style={{ width: `${Math.min(p.value, 100)}%`, backgroundColor: p.color }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-right">{p.value.toFixed(1)} grains/m³</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-400 italic">Données polliniques en cours de mise à jour pour cette région.</div>
          )}
        </Card>

        {/* --- SECTION 3 : VULGARISATION --- */}
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="font-bold flex items-center gap-2 text-gray-800 dark:text-gray-200 uppercase text-sm tracking-widest">
              <Info className="w-4 h-4" /> Comprendre les polluants
            </h3>
            {POLLUTANTS.map(p => (
              <div key={p.key} className="text-sm">
                <span className="font-bold text-blue-600">{p.label} :</span> 
                <span className="text-gray-600 dark:text-gray-400 ml-1">{p.detail}</span>
              </div>
            ))}
          </div>

          <div className="bg-orange-50/50 dark:bg-orange-950/10 p-6 rounded-2xl border border-orange-100 dark:border-orange-900/30">
            <h3 className="font-bold text-orange-800 dark:text-orange-400 mb-2">💡 Le saviez-vous ?</h3>
            <p className="text-sm text-orange-800/80 dark:text-orange-300/70 leading-relaxed">
              L'ozone (O₃) est un polluant "secondaire" : il ne sort pas des pots d'échappement mais se crée par réaction chimique entre les gaz et le soleil. C'est pour cela qu'il est plus élevé les après-midi d'été !
            </p>
            <a href="https://www.pollens.fr/" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-orange-600 hover:underline">
              Source officielle : RNSA / Pollens.fr <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
};

export default HealthAlertsPage;